import type {
	Autocomplete,
	SearchPage,
	LandingPage,
	ProductPage,
} from "@apptus/esales-api";
import {TTLCache} from "@isaacs/ttlcache";
// import { useWindowSize } from '@vueuse/core';
import {
	addDays,
	addHours,
	addMinutes,
	addYears,
	formatISO,
	isAfter,
	parseISO,
	startOfDay,
	startOfHour,
} from "date-fns";
import {nanoid} from "nanoid";
import {omit} from "es-toolkit";
import type {AsyncDataOptions} from "nuxt/app";
import {useProfile} from "../store/profile";

import {computed} from "vue";
import {useAsyncData, useCookie, useRuntimeConfig} from "nuxt/app";

export interface RecommendationListSettings {
	id: string;
	algorithm?:
		| "TOP_PRODUCTS"
		| "PERSONAL"
		| "ALTERNATIVES"
		| "UPSELL"
		| "CART"
		| "NEWEST_PRODUCTS"
		| "STYLE_WITH"
		| "FAVORITES"
		| "MORE_FROM_SERIES"
		| "RECENTLY_VIEWED";
	label?: string;
	limit?: number;
	params?: unknown;
	productRules?: string;
	showMoreLink?: string;
	visualization?: "CAROUSEL" | "GRID";
}

interface ElevateSession {
	key: string;
	expires: string;
}

// We store the *promise* returned by the first fetch with the given key, so that if a lot of fetches come in concurrently, subsequent fetches with the same key can return the result of the same promise once it resolves. This solves the issue when multiple identical fetches come in before we've had time to cache the result. Another option to solve the issue could have been to throttle the fetching itself.
const cache = new TTLCache<string, Promise<unknown>>({
	ttl: 60 * 1000,
	// Keep a reasonable max amount of items at a time - we don't want this to risk using up the server's RAM.
	max: 50,
});

const getCacheKey = (
	method: "GET" | "POST",
	url: string,
	params: Record<string, unknown> = {},
	body: Record<string, unknown> = {},
) => {
	const cacheParams = omit(params, [
		"customerKey",
		"sessionKey",
		"touchpoint",
		"viewId",
	]);
	return method + url + JSON.stringify(cacheParams) + JSON.stringify(body);
};

export function useElevate() {
	// const { width } = useWindowSize();
	const profileStore = useProfile();
	const {categories} = useCookieFirst();
	const isTrackingAllowed = computed(() => categories.value?.advertising);
	const sessionCookie = useCookie<ElevateSession | undefined>(
		"elevate-session",
		{
			expires: addYears(new Date(), 1),
		},
	);
	const previewCookie = useCookie<"yes" | "no" | undefined>("elevate-preview");

	const rangeKeys = ["price", "width", "height", "depth", "weight"];

	const convertFilters = (
		filters: Record<string, string[]>,
	): Record<string, string> => {
		const result: Record<string, string> = {};

		for (const [key, values] of Object.entries(filters)) {
			if (values.length === 0) {
				continue;
			}

			if (rangeKeys.includes(key)) {
				const [min, max] = values;

				if (min !== undefined) {
					result[`f.${key}.min`] = min;
				}

				if (max !== undefined) {
					result[`f.${key}.max`] = max;
				}
			} else {
				result[`f.${key}`] = values.join("|");
			}
		}

		return result;
	};

	const {
		public: {
			elevate: {clusterId, locale, market},
		},
	} = useRuntimeConfig();

	// The code of elevate card components defines whether we need these attributes at the product level, variant level, or both. Product level is the product cards shown in our listings, while several variants may possibly collapsed into one product card. We need to handle some attributes at the variant level because they can't be aggregated easily / logically into the product level.
	const customAttributeList = [
		"id",
		"sku",
		"title",
		"type",
		"specification",
		"promotion",
		"delivery_estimate_days",
		"clubPrice",
		"variant.clubPrice",
		"clubPriceDiscount",
		"variant.clubPriceDiscount",
		"productBadges",
		"lowest_price",
		"variant.lowest_price",
		"rating",
		"categories",
		"facet_categories",
		"brand_logo",
		"brand_title",
		"productClass",
		"isNewProduct",
		"isInCampaign",
		"inventoryState",
		"variant.inventoryState",
		"hasOver3Colors",
		"colorOptions",
		"variant.isAvailableForPurchase",
		"variant.isAvailableQuickly",
	];

	const baseUrl = `https://${clusterId}.api.esales.apptus.cloud/api/storefront/v3/`;

	const isPreviewEnabled = computed(() => previewCookie.value === "yes");

	const setPreviewState = (value: boolean) => {
		previewCookie.value = value ? "yes" : "no";
	};

	const getExpirationTime = (isNotification: boolean) => {
		// https://docs.elevate.voyado.cloud/elevate/4/integration/behavioral-data/visitor-identification/#sessions

		const dates: Date[] = [];

		if (isNotification) {
			// hourly expiration
			dates.push(startOfHour(addHours(new Date(), 2)));

			// UTC midnight expiration
			const midnightExpiry = addDays(startOfDay(new Date()), 2);
			midnightExpiry.setUTCHours(0, 0, 0, 0);
			dates.push(midnightExpiry);
		} else {
			// 15 minute inactivity timeout
			dates.push(addMinutes(new Date(), 15));
		}

		return new Date(Math.min(...dates.map((date) => date.getTime())));
	};

	const getSessionKey = (isNotification = false) => {
		const newExpiration = getExpirationTime(isNotification);

		// Generate new session key if session data is missing
		if (!sessionCookie.value) {
			sessionCookie.value = {
				key: nanoid(),
				expires: formatISO(newExpiration),
			};
		}

		const currentExpiration = parseISO(sessionCookie.value!.expires);

		// Generate new session key if current session key is expired
		if (isAfter(new Date(), currentExpiration)) {
			sessionCookie.value = {
				key: nanoid(),
				expires: formatISO(newExpiration),
			};
		}

		// Update expiration time if new expiration is later than current expiration
		if (isAfter(newExpiration, currentExpiration)) {
			sessionCookie.value = {
				...sessionCookie.value,
				expires: formatISO(newExpiration),
			};
		}

		return sessionCookie.value!.key;
	};

	// These methods can be called by lazy components (which load in CSR a bit after the rest of the page has been rendered), which means Nuxt's fetch key caching won't always take effect on these. To ensure that we don't get unnecessary re-fetches of the same data, and to improve overall performance too (no need to fetch the rarely-changing data for every request), we use a more robust cache method for these calls.

	async function doGet<T>(
		url: string,
		parameters: Record<string, unknown>,
		asyncOptions: AsyncDataOptions<T> = {},
	) {
		const sessionKey = getSessionKey();

		const reqParameters = {
			customerKey: profileStore.profile?.id || sessionKey,
			market,
			sessionKey,
			locale,
			// This was causing problems when it would always be some value on SSR but then if on CSR it was different, the fetch would return "null" as its data even if the fetching was awaited. This caused problems somewhere, like the product search page being empty on some screen sizes if you reload the page after initial searching. We could maybe get around this with `watch: true`
			// touchpoint: width.value >= 1024 ? 'DESKTOP' : 'MOBILE',
			touchpoint: "DESKTOP",
			viewId: previewCookie.value === "yes" ? "preview" : undefined,
			...parameters,
		};
		const reqUrl = baseUrl + url;

		const cacheKey = getCacheKey("GET", reqUrl, reqParameters);

		return await useAsyncData<T, unknown, T>(
			cacheKey,
			async () => {
				const cached = cache.get(cacheKey);
				if (cached !== undefined) {
					const res = await cached;
					return res as T;
				}

				const resPromise = $fetch<T>(baseUrl + url, {
					params: reqParameters,
				}) as Promise<T>;

				cache.set(cacheKey, resPromise);

				return await resPromise;
			},
			asyncOptions,
		);
	}

	async function doPost<T>(
		url: string,
		data: Record<string, unknown>,
		parameters: Record<string, unknown> = {},
		isNotification: boolean = false,
		useCache = false,
	) {
		const sessionKey = getSessionKey(isNotification);

		const reqParameters = {
			customerKey: profileStore.profile?.id || sessionKey,
			market,
			sessionKey,
			locale,
			// touchpoint: width.value >= 1024 ? 'DESKTOP' : 'MOBILE',
			touchpoint: "DESKTOP",
			viewId: previewCookie.value === "yes" ? "preview" : undefined,
			...parameters,
		};
		const reqUrl = baseUrl + url;

		const cacheKey = getCacheKey("POST", reqUrl, reqParameters, data);

		if (useCache) {
			const cached = cache.get(cacheKey);
			if (cached !== undefined) {
				const res = await cached;
				return res as T;
			}
		}

		const resPromise = $fetch<T>(reqUrl, {
			method: "POST",
			params: reqParameters,
			body: data,
		});

		if (useCache) {
			cache.set(cacheKey, resPromise);
		}

		return await resPromise;
	}

	const getAutocompleteSuggestions = async (query: string) =>
		await doGet<Autocomplete>("queries/autocomplete", {
			q: query,
			notify: isTrackingAllowed.value,
			presentCustom: customAttributeList.join("|"),
		});

	const getSearchResults = async (
		query: string,
		origin?: string,
		skip?: number,
		limit?: number,
		sort?: string,
		filters: Record<string, string[]> = {},
	) =>
		await doGet<SearchPage>("queries/search-page", {
			q: query,
			origin,
			skip,
			limit,
			sort,
			notify: isTrackingAllowed.value,
			presentCustom: customAttributeList.join("|"),
			...convertFilters(filters),
		});

	const getLandingPage = async (
		reference: string,
		skip?: number,
		limit?: number,
		sort?: string,
		filters: Record<string, string[]> = {},
		q?: string,
		asyncOptions?: AsyncDataOptions<LandingPage>,
	) =>
		await doGet<LandingPage>(
			"queries/landing-page",
			{
				pageReference: reference,
				skip,
				limit,
				sort,
				q,
				notify: isTrackingAllowed.value,
				presentCustom: customAttributeList.join("|"),
				...convertFilters(filters),
			},
			asyncOptions,
		);

	const getProductPage = async (
		productKey: string,
		recommendationLists: RecommendationListSettings[],
	) =>
		await doPost<ProductPage>(
			"queries/product-page",
			{
				recommendationLists,
			},
			{
				productKey,
				notify: isTrackingAllowed.value,
				presentCustom: customAttributeList.join("|"),
			},
			undefined,
			true,
		);

	const notifyClick = async (ticket: string) => {
		if (!isTrackingAllowed.value) {
			return;
		}

		await doPost<never>(
			"notifications/click",
			{
				ticket,
			},
			{},
			true,
		);
	};

	return {
		getAutocompleteSuggestions,
		getSearchResults,
		getLandingPage,
		getProductPage,
		notifyClick,
		getSessionKey,
		getExpirationTime,
		setPreviewState,
		isPreviewEnabled,
	};
}
